import React, { useCallback, useContext, useRef } from 'react';
import { Box, CircularProgress, Grid, makeStyles } from '@material-ui/core';
import { createForm } from 'final-form';
import { Form, FormSpy } from 'react-final-form';
import { KeyboardDatePicker, Select, TextField } from 'mui-rff';
import {
  fieldLengthValidator,
  formatDate,
  isValidDate
} from '../../../core/utils';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import { TextInput, useGetList } from 'react-admin';
import {
  emailFormatter,
  emailParser,
  STEP,
  validateEmails,
  validateFundIdRff
} from './utils';
import { LocalizedUtils } from '../../../core/components/forked/input/DateInput';
import { ErrorIcon } from '../../../core/themes/icons/ErrorIcon';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import FundApi from '../../../core/apis/FundApi';
import { isEmpty } from 'lodash';
import { CreationFundContext } from './CreationFundContext';
import omit from 'lodash/omit';
import { fundStatus } from '../FundLookups';

const fundNameMaxLength = 256;
const fundIdMaxLength = 20;

const useStyle = makeStyles({
  formGroup: {
    marginBlock: '16px'
  },
  center: {
    display: 'grid',
    placeItems: 'center',
    height: '66%'
  }
});

const FundDetailsStep = ({
  setIsNextAvailable,
  setIsSpinnerOnNextButton,
  onSubmitEnd,
  setIsFirstStepPristine,
  fundCreatedRef,
  setListOfSteps,
  listOfSteps
}) => {
  const classes = useStyle();
  const notifyServerError = useNotifyServerError();
  const { fundContext, setFundContext } = useContext(CreationFundContext);
  const formStateRef = useRef({});

  const onSubmit = useCallback(
    async values => {
      const pristine = formStateRef.current.pristine;
      setIsSpinnerOnNextButton(true);

      // First submission, post request
      if (!pristine && fundContext.fund === undefined) {
        const id = values.id;
        const payload = {
          ...values,
          inceptionDate: formatDate(values.inceptionDate)
        };
        try {
          const response = await FundApi.create(payload, id);
          setFundContext({ ...fundContext, fund: response });
          fundCreatedRef.current = response;
          setIsNextAvailable(true);
          setIsSpinnerOnNextButton(false);
          const updatedListOfStep = listOfSteps.map(step => {
            if (step.name === STEP.fundDetails) {
              return { ...step, completed: true };
            }
            return step;
          });
          setListOfSteps(updatedListOfStep);
          onSubmitEnd();
        } catch (e) {
          notifyServerError(e, undefined);
          setIsSpinnerOnNextButton(false);
        }
      }

      // Fund already created
      if (!pristine && fundContext.fund !== undefined) {
        const id = fundContext.fund.id;
        const payload = {
          ...omit(values, 'id'),
          inceptionDate: formatDate(values.inceptionDate)
        };

        try {
          const response = await FundApi.edit(payload, id);
          setFundContext({ ...fundContext, fund: response });
          setIsNextAvailable(true);
          setIsSpinnerOnNextButton(false);
          onSubmitEnd();
        } catch (e) {
          notifyServerError(e, undefined);
          setIsSpinnerOnNextButton(false);
        }
      }

      // If fund has already been created and there is no change in the form.
      if (pristine && fundContext.fund !== undefined) {
        setIsSpinnerOnNextButton(false);
        onSubmitEnd();
      }
    },
    [
      setIsSpinnerOnNextButton,
      fundContext,
      setFundContext,
      fundCreatedRef,
      setIsNextAvailable,
      listOfSteps,
      setListOfSteps,
      onSubmitEnd,
      notifyServerError
    ]
  );

  const {
    data: dataFundClasses,
    loading: fundClassesLoading,
    error: fundClassesError
  } = useGetList('fundclasses', null, { field: 'description', order: 'ASC' });

  const fundClasses = Object.values(dataFundClasses).map(fundClass => ({
    value: fundClass.classCode,
    label: fundClass.description
  }));

  const {
    data: dataCurrencies,
    loading: currenciesLoading,
    error: currenciesError
  } = useGetList('currencies', null, 'ASC');

  const currencies = Object.values(dataCurrencies).map(currency => ({
    value: currency.id,
    label: currency.code
  }));

  const {
    data: dataFundFamilies,
    loading: fundFamiliesLoading,
    error: fundFamiliesError,
    total: fundFamilyCount
  } = useGetList('fundfamilies', null, undefined, {
    orphanVisibility: 'ALL_VISIBLE',
    orderBy: 'id',
    orderDirection: 'DESC'
  });

  const fundFamilies = Object.values(dataFundFamilies).map(fundFamily => ({
    value: fundFamily.id,
    label: fundFamily.name
  }));

  const initialValues = isEmpty(fundContext.fund) ? null : fundContext.fund;
  const formRef = useRef(
    createForm({
      onSubmit: onSubmit,
      initialValues
    })
  );
  const required = value => (value ? undefined : 'Required');
  const composeValidators = (...validators) => (value, values, fieldState) =>
    validators.reduce(
      (error, validator) => error || validator(value, values, fieldState),
      undefined
    );

  if (fundClassesLoading || currenciesLoading || fundFamiliesLoading) {
    return (
      <Box className={classes.center}>
        <CircularProgress />
      </Box>
    );
  } else if (fundClassesError || currenciesError || fundFamiliesError) {
    return (
      <Box className={classes.center}>
        <ErrorIcon />
      </Box>
    );
  } else {
    return (
      <Form form={formRef.current}>
        {({ handleSubmit, submitting }) => (
          <>
            <FormSpy
              subscription={{ valid: true, pristine: true, submitting: true }}
              onChange={props => {
                setIsNextAvailable(props.valid && !props.submitting);
                setIsFirstStepPristine(props.pristine);
                formStateRef.current = { pristine: props.pristine };
              }}
            />
            <form onSubmit={handleSubmit} id="formFundDetails" noValidate>
              <Grid container>
                <Grid item xs={12} className={classes.formGroup}>
                  <TextField
                    name="name"
                    label="Fund Name"
                    fieldProps={{
                      validate: composeValidators(
                        fieldLengthValidator(fundNameMaxLength),
                        required
                      )
                    }}
                    required
                    disabled={submitting}
                    id="fund-name"
                  />
                </Grid>
                <Grid item xs={12} className={classes.formGroup}>
                  <Select
                    name="classCode"
                    label="Fund Type"
                    data={fundClasses}
                    fieldProps={{ validate: required }}
                    required
                    disabled={submitting}
                    id="fund-type"
                  />
                </Grid>
                <Grid item xs={12} className={classes.formGroup}>
                  <Select
                    name="currency"
                    label="Currency"
                    data={currencies}
                    fieldProps={{ validate: required }}
                    required
                    disabled={submitting}
                    id="fund-currency"
                  />
                </Grid>
                <Grid item xs={12} className={classes.formGroup}>
                  <TextField
                    name="id"
                    label="Fund ID"
                    fieldProps={{
                      validate: composeValidators(
                        fieldLengthValidator(fundIdMaxLength),
                        required,
                        validateFundIdRff
                      )
                    }}
                    required
                    disabled={submitting || !isEmpty(fundContext.fund)}
                    id="fund-id"
                  />
                </Grid>
                <Grid item xs={12} className={classes.formGroup}>
                  <Select
                    name="status"
                    label="Fund Status"
                    data={fundStatus}
                    fieldProps={{ validate: required }}
                    required
                    disabled={submitting}
                    id="fund-status"
                  />
                </Grid>
                {fundFamilyCount > 0 && (
                  <Grid item xs={12} className={classes.formGroup}>
                    <Select
                      name="fundFamilyId"
                      label="Fund Family"
                      data={fundFamilies}
                      disabled={submitting}
                      id="fund-family-id"
                    />
                  </Grid>
                )}
                <Grid item xs={12} className={classes.formGroup}>
                  <MuiPickersUtilsProvider utils={LocalizedUtils}>
                    <KeyboardDatePicker
                      label="Fund Inception Date"
                      name="inceptionDate"
                      required
                      placeholder="MM/dd/yyyy"
                      format="MM/dd/yyyy"
                      fieldProps={{
                        validate: composeValidators(required, isValidDate)
                      }}
                      parse={formatDate}
                      disabled={submitting}
                      id="fund-inception-date"
                    />
                  </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={12} className={classes.formGroup}>
                  <TextInput
                    source="emailAddresses"
                    label="Fund Contact Email"
                    format={emailFormatter}
                    parse={emailParser}
                    validate={[validateEmails, fieldLengthValidator(500)]}
                    allowEmpty
                    options={{ autoComplete: 'off' }}
                    variant="standard"
                    helperText={
                      'If you input multiple email addresses, separate them with semicolon (;)'
                    }
                    disabled={submitting}
                    id="fund-email-adresses"
                  />
                </Grid>
              </Grid>
            </form>
          </>
        )}
      </Form>
    );
  }
};

export default FundDetailsStep;
